<script>
import {
  pageFicheMagasinEmits,
  pageFicheMagasinProps,
  usePageFicheMagasin
} from "./mixin";
import Titre from "@/components/atoms/titre";
import IconShipping from "@/components/molecules/icon-shipping";
import Container from "@/components/atoms/container";
import Divider from "@/components/atoms/divider";
import Button from "@/components/atoms/button";
import Prospectus from "@/components/molecules/prospectus";
import LinkTo from "@/components/atoms/link-to";
import PrixCarburant from "@/components/molecules/prix-carburant";
import GridRow from "@/components/molecules/grid/grid-row";
import GridCol from "@/components/molecules/grid/grid-col";
import { useFicheMagasinStructuredData } from "@/mixins/seo/ficheMagasinStructuredData";
import HtmlParser from "@/components/organisms/cms-container/html-parser";
import TemplateMagasin from "@/components/organisms/template-page-magasin/default";
import WithdrawalPoint from "@/components/organisms/withdrawal-point";
import Popin from "@/components/molecules/popin";
import PopinClickAndCollect from "@/components/organisms/popin-click-collect";
import PopinTraiteurDeFetes from "@/containers/popin-traiteur-de-fetes";
import PopinTraiteurPermanent from "@/containers/popin-traiteur-permanent";
import PopinClickCollectTraiteurFetes from "@/components/organisms/popin-cc-traiteur-fetes";
import PopinClickCollectTraiteurPermanent from "@/components/organisms/popin-cc-traiteur-permanent";
import MeaCoraproMagasin from "@/containers/mise-en-avant-corapro";

export default {
  name: "FicheMagasin",
  components: {
    Titre,
    IconShipping,
    Container,
    Divider,
    Button,
    Prospectus,
    LinkTo,
    PrixCarburant,
    GridCol,
    GridRow,
    TemplateMagasin,
    HtmlParser,
    WithdrawalPoint,
    Popin,
    PopinClickAndCollect,
    PopinTraiteurDeFetes,
    PopinTraiteurPermanent,
    PopinClickCollectTraiteurFetes,
    PopinClickCollectTraiteurPermanent,
    MeaCoraproMagasin
  },
  props: {
    ...pageFicheMagasinProps()
  },
  emits: ["openOrcloseStoreLocator", ...pageFicheMagasinEmits()],
  setup(props, context) {
    const traiteurFete = ref(null);
    const traiteurPermanent = ref(null);
    const {
      showWithdrawalPoints,
      withdrawalPoints,
      showPlanningClickCollect,
      showPlanningClickCollectTraiteur,
      showPlanningClickCollectTraiteurNoel,
      isNational,
      modesDeCommande,
      changeShop,
      toggleWithdrawalPoints,
      togglePlanningTabClickCollect,
      togglePlanningTabClickCollectTraiteur,
      togglePlanningTabClickCollectTraiteurNoel,
      closeWithdrawalPopin,
      getPushData,
      getPushList,
      getPushTitle,
      mounted,
      ShopPageCodes,
      isFuelAvailable
    } = usePageFicheMagasin(props, context, traiteurFete, traiteurPermanent);

    const { head } = useFicheMagasinStructuredData(props.magasin);
    useHead(head);

    onMounted(mounted);

    return {
      isFuelAvailable,
      showWithdrawalPoints,
      withdrawalPoints,
      showPlanningClickCollect,
      showPlanningClickCollectTraiteur,
      showPlanningClickCollectTraiteurNoel,
      isNational,
      modesDeCommande,
      changeShop,
      toggleWithdrawalPoints,
      togglePlanningTabClickCollect,
      togglePlanningTabClickCollectTraiteur,
      togglePlanningTabClickCollectTraiteurNoel,
      closeWithdrawalPopin,
      getPushData,
      getPushList,
      getPushTitle,
      ShopPageCodes,
      traiteurFete,
      traiteurPermanent
    };
  }
};
</script>

<template>
  <TemplateMagasin
    :is-pro="isPro"
    :is-national="isNational"
    :magasin="magasin"
    :attached-shop-id="attachedShopId"
    :has-blog="articles && articles.length > 0"
    :push-zones="pushZones"
    @openOrcloseStoreLocator="$emit('openOrcloseStoreLocator')"
    @changeShop="(storeId) => changeShop(storeId)"
  >
    <PopinClickCollectTraiteurFetes
      ref="traiteurFete"
      :magasin="magasin"
    />
    <PopinClickCollectTraiteurPermanent
      ref="traiteurPermanent"
      :magasin="magasin"
    />
    <PopinTraiteurDeFetes
      :show-planning-click-collect-traiteur="showPlanningClickCollectTraiteurNoel"
      @closed="showPlanningClickCollectTraiteurNoel = false"
    />
    <PopinTraiteurPermanent
      :visible="showPlanningClickCollectTraiteur"
      @closed="showPlanningClickCollectTraiteur = false"
    />
    <Popin
      :visible="showWithdrawalPoints"
      size="lg"
      radius="md"
      shadow="lg"
      aria-label="Liste des points retrait"
      @closed="closeWithdrawalPopin()"
    >
      <Titre
        tag="h2"
        variant="primary"
        class="u-mb-md u-text-center"
        size="xs"
      >
        Liste des points retrait
      </Titre>
      <WithdrawalPoint :withdrawal-points="withdrawalPoints" />
    </Popin>
    <PopinClickAndCollect
      :show-planning-click-collect="showPlanningClickCollect"
      :magasin="magasin"
      @closed="showPlanningClickCollect = false"
    />
    <Container v-if="magasin.attributes.modes_commande && magasin.attributes.modes_commande.length >0 && !isPro">
      <Titre
        tag="h2"
        size="xs"
        class="u-text-center u-mb-md"
      >
        Modes de livraison disponibles dans le magasin {{ magasin.attributes.magasin }}
      </Titre>

      <GridRow class="">
        <GridCol
          v-for="mdc in modesDeCommande"
          :key="mdc.type"
          cols="4"
          lg="5"
          md="6"
          sm="12"
          offset="2"
          offset-lg="1"
          offset-md="0"
        >
          <div :class="mdc.type === 'CC_TRAITEUR_NOEL' || mdc.type === 'CC_TRAITEUR_PERMANENT' ? 'u-flex u-flex-align-start u-mb-sm' :'u-flex u-flex-align-center u-mb-sm'">
            <IconShipping
              :type="mdc.type"
              variant="primary"
            />
            <div class="u-ml-sm">
              <p class="u-font-13">
                {{ mdc.label_long }}
              </p>
              <Button
                v-if="mdc.type==='PRP' || mdc.type=== 'PRC' || mdc.type=== 'PRC_DELIPOP'"
                link
                variant="primary"
                align="left"
                @click="toggleWithdrawalPoints(mdc.services)"
              >
                Voir la liste des points de retrait
              </Button>
              <Button
                v-if="mdc.type==='CEV'"
                link
                variant="primary"
                align="left"
                @click="toggleWithdrawalPoints(mdc.services)"
              >
                Voir les adresses Drive piéton
              </Button>
              <Button
                v-if="mdc.type ==='CC'"
                link
                variant="primary"
                align="left"
                @click="togglePlanningTabClickCollect()"
              >
                En savoir plus sur le Click & Collect
              </Button>
              <template v-if="mdc.type ==='CC_TRAITEUR_NOEL'">
                <Button
                  link
                  variant="primary"
                  align="left"
                  @click="togglePlanningTabClickCollectTraiteurNoel()"
                >
                  En savoir plus sur le Click & Collect Traiteur
                </Button>
                <Button
                  link
                  variant="primary"
                  align="left"
                  class="u-mt-xxs"
                  @click="$refs.traiteurFete.show()"
                >
                  Voir l’emplacement du stand Traiteur de Fêtes
                </Button>
              </template>
              <template v-if="mdc.type ==='CC_TRAITEUR_PERMANENT'">
                <Button
                  link
                  variant="primary"
                  align="left"
                  @click="togglePlanningTabClickCollectTraiteur()"
                >
                  En savoir plus sur le Click & Collect Traiteur
                </Button>
                <Button
                  link
                  variant="primary"
                  align="left"
                  class="u-mt-xxs"
                  @click="$refs.traiteurPermanent.show()"
                >
                  Voir l’emplacement du stand Traiteur
                </Button>
              </template>
            </div>
          </div>
        </GridCol>
      </GridRow>
    </Container>
    <div class="u-mt-lg">
      <Divider dotted />
      <div class="u-pt-lg">
        <MeaCoraproMagasin
          :magasin="magasin"
          is-fiche-magasin
        />
      </div>
    </div>
    <client-only>
      <template v-if="getPushData(ShopPageCodes.FM_MEA_1).html_content || getPushData(ShopPageCodes.FM_MEA_1, 2).html_content || getPushData(ShopPageCodes.FM_MEA_1, 3).html_content">
        <Divider
          v-if="getPushData(ShopPageCodes.FM_MEA_1).html_content || getPushData(ShopPageCodes.FM_MEA_1, 2).html_content || getPushData(ShopPageCodes.FM_MEA_1, 3).html_content"
          class="u-mt-lg u-mb-lg20"
        />
        <GridRow
          class="u-mb-sm"
          :class="getPushList(ShopPageCodes.FM_MEA_1).length < 3 && 'u-flex-justify-center'"
        >
          <GridCol
            v-if="getPushData(ShopPageCodes.FM_MEA_1).html_content"
            cols="4"
            sm="12"
            class="u-mb-md@sm"
          >
            <HtmlParser
              :context="`FM MEA 1 :${getPushTitle(ShopPageCodes.FM_MEA_1)}`"
              :promo-printing="true"
              :push-data="getPushData(ShopPageCodes.FM_MEA_1)"
            />
          </GridCol>
          <GridCol
            v-if="getPushData(ShopPageCodes.FM_MEA_1, 2).html_content"
            cols="4"
            sm="12"
            class="u-mb-md@sm"
          >
            <HtmlParser
              :context="`FM MEA 2 :${getPushTitle(ShopPageCodes.FM_MEA_1)} `"
              :promo-printing="true"
              :push-data="getPushData(ShopPageCodes.FM_MEA_1, 2)"
            />
          </GridCol>
          <GridCol
            v-if="getPushData(ShopPageCodes.FM_MEA_1, 3).html_content"
            cols="4"
            sm="12"
            class="u-mb-md@sm"
          >
            <HtmlParser
              :context="`FM MEA 3 :${getPushTitle(ShopPageCodes.FM_MEA_1, 3)}`"
              :promo-printing="true"
              :push-data="getPushData(ShopPageCodes.FM_MEA_1, 3)"
            />
          </GridCol>
        </GridRow>
      </template>
    </client-only>
    <template v-if="prospectus && prospectus.length>0">
      <Divider
        dotted
        class="u-mt-lg u-mb-lg20"
      />
      <Titre
        variant="primary"
        tag="h2"
        class="u-text-center u-mb-sm"
      >
        Faites des économies en consultant les prospectus
      </Titre>
      <div class="u-flex u-flex-justify-center u-flex-wrap u-mb-sm">
        <template
          v-for="(item,index) in prospectus"
          :key="item.id"
        >
          <Prospectus
            v-if="index<5"
            :img-path="item.attributes.cover"
            :period="item.attributes.period"
            :title="item.attributes.title"
            :link="item.attributes.url"
            :start-date="item.attributes.start_at"
            :page-type="trackingCodes.PAGETYPE.ficheMagasin"
            img-width="159"
            class="u-ml-sm u-mr-sm u-mb-md"
          />
        </template>
      </div>
      <div class="u-text-center">
        <LinkTo
          variant="primary"
          to="/prospectus"
        >
          Voir tous les prospectus
        </LinkTo>
      </div>
    </template>
    <template v-if="(fuel && fuel.length >0)">
      <Divider
        dotted
        class="u-mt-lg u-mb-lg"
      />
      <Titre
        variant="primary"
        tag="h2"
        class="u-text-center u-mb-lg"
      >
        Prix des carburants
      </Titre>
      <div class="u-flex u-flex-wrap u-flex-justify-center c-prix-carburant-container">
        <template
          v-for="fuelItem in fuel"
          :key="fuelItem.id"
        >
          <PrixCarburant
            :type="fuelItem.attributes.libelle"
            :name="fuelItem.attributes.libelle"
            :price="fuelItem.attributes.prix"
            class="u-m-xs"
          />
        </template>
      </div>
      <div>
        <p
          v-if="isFuelAvailable"
          class="u-text-center u-font-15 u-mb-lg"
        >
          Pompes automatiques 24h/24 (hors fioul, commandable auprès du Village des Services).
        </p>
        <p
          v-else
          class="u-text-center u-font-15 u-mb-lg"
        >
          Pompes automatiques 24h/24
        </p>
      </div>
    </template>
  </TemplateMagasin>
</template>

<style lang="scss" scoped>
.c-prix-carburant-container {
  max-width: 90rem;
  margin-left: auto;
  margin-right: auto;
}
</style>
